/**
 * Stripe Membership Migration Service
 * 
 * This script migrates existing Stripe subscriptions from your old CRM
 * to the new Firebase-based system WITHOUT re-charging users.
 * 
 * Usage:
 * 1. Run with dryRun=true first to see what will happen
 * 2. Review the results carefully
 * 3. Run with dryRun=false to perform actual migration
 */

import { getFirestore, collection, getDocs, updateDoc, doc, query, where } from 'firebase/firestore';
import { ACCOUNT_TYPES } from "../hooks/useAccountType";

// NOTE: Stripe API calls must be done from backend/Cloud Functions for security
// This is a client-side template - actual implementation should be in Cloud Functions

const db = getFirestore();

/**
 * Map Stripe subscription to CRM account type
 */
export function getAccountTypeFromSubscription(subscription) {
  const productName = subscription.plan?.product?.name?.toLowerCase() || '';
  const nickname = subscription.plan?.nickname?.toLowerCase() || '';
  const amount = subscription.plan?.amount || 0; // in cents
  
  // Customize these mappings based on your Stripe product names
  if (productName.includes(ACCOUNT_TYPES.SENIOR_BROKER) || nickname.includes(ACCOUNT_TYPES.SENIOR_BROKER)) {
    return ACCOUNT_TYPES.SENIOR_BROKER;
  }
  if (productName.includes(ACCOUNT_TYPES.BROKER) || nickname.includes(ACCOUNT_TYPES.BROKER)) {
    return ACCOUNT_TYPES.BROKER ;
  }
  if (productName.includes(ACCOUNT_TYPES.LEADER) || nickname.includes(ACCOUNT_TYPES.LEADER)) {
    return ACCOUNT_TYPES.LEADER;
  }
  
  // Fallback based on price
  if (amount >= 9900) return ACCOUNT_TYPES.SENIOR_BROKER; // $99+
  if (amount >= 4900) return ACCOUNT_TYPES.BROKER ; // $49+
  if (amount >= 2900) return ACCOUNT_TYPES.LEADER; // $29+
  
  return 'Paid'; // Default for active subscriptions
}

/**
 * Find Firebase user by email
 */
export async function findUserByEmail(email) {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('email', '==', email.toLowerCase()));
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  return snapshot.docs[0];
}

/**
 * Find Firebase user by Stripe Customer ID
 */
export async function findUserByStripeCustomerId(stripeCustomerId) {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('stripeCustomerId', '==', stripeCustomerId));
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  return snapshot.docs[0];
}

/**
 * Update Firebase user with Stripe subscription data
 */
export async function updateUserWithStripeData(userId, subscriptionData) {
  const userRef = doc(db, 'users', userId);
  
  const updateData = {
    // Stripe identifiers
    stripeCustomerId: subscriptionData.customerId,
    stripeSubscriptionId: subscriptionData.subscriptionId,
    
    // Account type
    plan: subscriptionData.plan,
    
    // Subscription details
    subscriptionStatus: subscriptionData.status,
    subscriptionPlan: subscriptionData.planName,
    subscriptionAmount: subscriptionData.amount,
    subscriptionInterval: subscriptionData.interval,
    subscriptionCurrency: subscriptionData.currency,
    
    // Dates
    subscriptionStartDate: subscriptionData.startDate,
    subscriptionCurrentPeriodStart: subscriptionData.currentPeriodStart,
    subscriptionCurrentPeriodEnd: subscriptionData.currentPeriodEnd,
    subscriptionCancelAt: subscriptionData.cancelAt || null,
    
    // Migration metadata
    migratedFromOldCRM: true,
    migrationDate: new Date(),
    migrationSource: 'stripe-legacy',
    
    // Timestamp
    updatedAt: new Date(),
  };
  
  await updateDoc(userRef, updateData);
  
  console.log(`✅ Updated user ${userId} with Stripe subscription`);
  
  return updateData;
}

/**
 * Check if user has active subscription
 */
export function isSubscriptionActive(subscriptionStatus) {
  return ['active', 'trialing'].includes(subscriptionStatus);
}

/**
 * Check if subscription is in grace period (past due but still has access)
 */
export function isSubscriptionInGracePeriod(subscriptionStatus) {
  return subscriptionStatus === 'past_due';
}

/**
 * Get user's effective account type based on subscription
 */
export function getEffectiveAccountType(profile) {
  if (!profile) return 'Free';
  
  // Check subscription status
  const status = profile.subscriptionStatus;
  
  // Active or in grace period - use their assigned account type
  if (isSubscriptionActive(status) || isSubscriptionInGracePeriod(status)) {
    return profile.plan || 'Paid';
  }
  
  // Canceled or inactive - downgrade to Free
  if (['canceled', 'incomplete', 'incomplete_expired', 'unpaid'].includes(status)) {
    return 'Free';
  }
  
  // Fallback to profile account type
  return profile.plan || 'Free';
}

/**
 * Format subscription for display
 */
export function formatSubscriptionDetails(profile) {
  if (!profile?.stripeSubscriptionId) {
    return {
      hasSubscription: false,
      status: 'No active subscription',
      renewsAt: null,
      amount: null,
    };
  }
  
  const status = profile.subscriptionStatus;
  const renewsAt = profile.subscriptionCurrentPeriodEnd;
  const amount = profile.subscriptionAmount;
  const interval = profile.subscriptionInterval;
  
  let statusText = 'Unknown';
  let statusColor = 'gray';
  
  if (isSubscriptionActive(status)) {
    statusText = 'Active';
    statusColor = 'green';
  } else if (isSubscriptionInGracePeriod(status)) {
    statusText = 'Payment Issue - Access Maintained';
    statusColor = 'yellow';
  } else if (status === 'canceled') {
    statusText = 'Canceled';
    statusColor = 'red';
  }
  
  return {
    hasSubscription: true,
    status: statusText,
    statusColor,
    renewsAt: renewsAt ? new Date(renewsAt.seconds * 1000) : null,
    amount,
    interval,
    plan: profile.subscriptionPlan || 'Membership',
  };
}

/**
 * Client-side helper to check subscription in real-time
 */
export async function checkUserSubscription(userId) {
  // This would call a Cloud Function that checks Stripe
  // For now, it reads from Firebase
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDocs(userRef);
  
  if (!userDoc.exists()) {
    return null;
  }
  
  const data = userDoc.data();
  return formatSubscriptionDetails(data);
}

// Export all functions for use in migration script and components
export default {
  getAccountTypeFromSubscription,
  findUserByEmail,
  findUserByStripeCustomerId,
  updateUserWithStripeData,
  isSubscriptionActive,
  isSubscriptionInGracePeriod,
  getEffectiveAccountType,
  formatSubscriptionDetails,
  checkUserSubscription,
};

